import React, { useState } from 'react'
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Snackbar,
  CircularProgress,
  Box,
} from '@mui/material'
import SendIcon from '@mui/icons-material/Send'
import { FormattedMessage } from 'react-intl'
import axiosInstance from '../../../../utils/axiosInstance'

interface SendPreviewLinkProps {
  orderId: string
  customerEmail?: string
}

const SendPreviewLink: React.FC<SendPreviewLinkProps> = ({ orderId, customerEmail }) => {
  const [dialogOpen, setDialogOpen] = useState(false)
  const [snackbarOpen, setSnackbarOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleOpenDialog = () => {
    setDialogOpen(true)
  }

  const handleCloseDialog = () => {
    setDialogOpen(false)
    setError(null)
  }

  const handleSendEmail = async () => {
    setLoading(true)
    setError(null)

    try {
      await axiosInstance.post('/send-preview-link', {
        orderId,
      })
      setDialogOpen(false)
      setSnackbarOpen(true)
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to send preview link')
    } finally {
      setLoading(false)
    }
  }

  const handleCloseSnackbar = () => {
    setSnackbarOpen(false)
  }

  return (
    <>
      <Button
        variant="outlined"
        size="small"
        startIcon={<SendIcon />}
        onClick={handleOpenDialog}
        sx={{ ml: 1 }}
      >
        <FormattedMessage id="send-preview-link" defaultMessage="Send Preview Link" />
      </Button>

      <Dialog open={dialogOpen} onClose={handleCloseDialog}>
        <DialogTitle>
          <FormattedMessage
            id="send-preview-link-title"
            defaultMessage="Send Preview Link via Email"
          />
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {customerEmail ? (
              <>
                <FormattedMessage
                  id="send-preview-link-confirmation"
                  defaultMessage="Are you sure you want to send the preview link to"
                />{' '}
                <strong>{customerEmail}</strong>?
              </>
            ) : (
              <FormattedMessage
                id="send-preview-link-confirmation-generic"
                defaultMessage="Are you sure you want to send the preview link to the customer?"
              />
            )}
          </DialogContentText>
          {error && (
            <Box sx={{ mt: 2, color: 'error.main' }}>
              <FormattedMessage id="error" defaultMessage="Error" />: {error}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} disabled={loading}>
            <FormattedMessage id="cancel" defaultMessage="Cancel" />
          </Button>
          <Button
            onClick={handleSendEmail}
            variant="contained"
            disabled={loading}
            startIcon={loading ? <CircularProgress size={16} /> : <SendIcon />}
          >
            <FormattedMessage id="send" defaultMessage="Send" />
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        message={
          <FormattedMessage
            id="preview-link-sent-success"
            defaultMessage="Preview link sent successfully"
          />
        }
      />
    </>
  )
}

export default SendPreviewLink
